import { useEffect, useId, useRef } from 'react';
import { ArrowRight, Mic, MicOff, RefreshCcw, Video, VideoOff } from 'lucide-react';

import { cn } from '@/lib/utils';
import { MyndwellMark } from '@/components/ui/brand-mark';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useLocalMediaPreview } from '../hooks/useLocalMediaPreview';
import type { CallStatusResponse } from '../services';

import { MicLevelBar } from './MicLevelBar';

interface PreJoinScreenProps {
  sessionId: string;
  mode: 'online' | 'helpline';
  callStatus: CallStatusResponse | undefined;
  isStatusError: boolean;
  onRefreshStatus: () => void;
  onJoin: (devices: {
    cameraId?: string;
    micId?: string;
    audioOnly?: boolean;
    /** Initial publish state from the lobby. Default true on both. */
    cameraEnabled?: boolean;
    micEnabled?: boolean;
  }) => void;
  onCancel: () => void;
}

/**
 * Pre-LiveKit screen. Renders a camera preview tile (online), device pickers,
 * mic level meter, and a snapshot of room status. Therapeutic-dusk aesthetic:
 * forest-dark surrounds, Roobert SemiMono italic headline, shadcn Select pickers,
 * custom segmented mic meter, amber-pulse waiting indicator.
 */
export function PreJoinScreen({
  sessionId: _sessionId,
  mode,
  callStatus,
  isStatusError,
  onRefreshStatus,
  onJoin,
  onCancel,
}: PreJoinScreenProps) {
  const preview = useLocalMediaPreview({ mode });
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = preview.videoStream ?? null;
  }, [preview.videoStream]);

  // Permission-denied / not-found fallback.
  if (preview.error) {
    return (
      <PermissionDeniedState
        mode={mode}
        kind={preview.error.kind}
        onRetry={preview.retry}
        onJoinAudioOnly={() => {
          onJoin({ cameraId: undefined, micId: undefined, audioOnly: true });
        }}
        onCancel={onCancel}
      />
    );
  }

  const waitingCopy = isStatusError
    ? "Couldn't load room state."
    : callStatus?.exists && callStatus.participants.length > 0
      ? 'Someone from your care team is in the room.'
      : 'Waiting for your care team to join.';

  const someoneHere = Boolean(callStatus?.exists && callStatus.participants.length > 0);

  const selectTriggerClass =
    'h-11 bg-[#0F1F1C]! text-white! border-white/10! hover:border-white/20! data-[state=open]:border-[#35816E]/60! focus:shadow-[0_0_0_2px_rgba(53,129,110,0.45)]! focus:border-[#35816E]!';

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-[#0B1614] text-white">
      <BackgroundGlow />

      <header className="relative z-10 flex items-center justify-between px-8 py-5">
        <MyndwellMark tone="light" size="sm" />
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-1 text-[13px] text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
        >
          Back to session
        </button>
      </header>

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-6 px-8 pb-4 lg:gap-8">
        <div className="fade-in slide-in-from-bottom-2 animate-in text-center duration-500">
          <h1 className="font-display text-[34px] leading-tight tracking-tight text-white italic md:text-[40px]">
            Ready to join the call?
          </h1>
          <p className="font-display mt-2 text-[14px] text-white/55 italic">
            Take a breath before we begin.
          </p>
        </div>

        <div className="flex w-full max-w-260 flex-col items-stretch gap-6 lg:flex-row lg:items-start lg:gap-10">
          {/* Left column — preview tile or audio-only card */}
          <div className="fade-in zoom-in-95 animate-in flex-1 duration-700">
            {mode === 'online' ? (
              <div
                className="relative w-full overflow-hidden rounded-xl border border-white/8 bg-[#0F1F1C] shadow-[0_12px_48px_rgba(0,0,0,0.5)]"
                style={{ aspectRatio: '16 / 9' }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-10 rounded-xl ring-1 ring-[#35816E]/15 ring-inset"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-8 z-0 rounded-[28px] bg-[radial-gradient(ellipse_at_center,rgba(53,129,110,0.22)_0%,transparent_60%)] blur-2xl"
                />
                {preview.videoStream && preview.cameraEnabled ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    data-testid="preview-video"
                    className="relative z-1 h-full w-full object-cover"
                  />
                ) : (
                  <div className="relative z-1 flex h-full items-center justify-center text-[13px] text-white/40">
                    <span className="font-display italic">
                      {preview.videoStream
                        ? 'Camera off — others won’t see you'
                        : 'Warming up the camera…'}
                    </span>
                  </div>
                )}

                {/* Mic + camera toggle pills — bottom-centred over the
                    preview tile so they read as call-in-progress controls
                    even before the user joins. Mirrors the live LiveKit
                    bar so muscle memory carries over. */}
                <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex items-center justify-center gap-2">
                  <PreviewToggle
                    enabled={preview.micEnabled}
                    onClick={() => {
                      preview.setMicEnabled(!preview.micEnabled);
                    }}
                    label={preview.micEnabled ? 'Mute microphone' : 'Unmute microphone'}
                    OnIcon={Mic}
                    OffIcon={MicOff}
                  />
                  <PreviewToggle
                    enabled={preview.cameraEnabled}
                    onClick={() => {
                      preview.setCameraEnabled(!preview.cameraEnabled);
                    }}
                    label={preview.cameraEnabled ? 'Turn camera off' : 'Turn camera on'}
                    OnIcon={Video}
                    OffIcon={VideoOff}
                  />
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center justify-center gap-5 rounded-xl border border-white/8 bg-[#0F1F1C] px-6 py-10">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#35816E]/15 text-[#6AA489]">
                    <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden="true">
                      <path
                        d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-display text-[18px] text-white italic">Audio-only call</p>
                    <p className="mt-0.5 text-[13px] text-white/55">
                      We&rsquo;ll connect you by voice.
                    </p>
                  </div>
                </div>
                <PreviewToggle
                  enabled={preview.micEnabled}
                  onClick={() => {
                    preview.setMicEnabled(!preview.micEnabled);
                  }}
                  label={preview.micEnabled ? 'Mute microphone' : 'Unmute microphone'}
                  OnIcon={Mic}
                  OffIcon={MicOff}
                />
              </div>
            )}
          </div>

          {/* Right column — device pickers, mic level, status */}
          <div className="fade-in slide-in-from-bottom-3 animate-in flex w-full flex-col gap-4 duration-700 lg:w-95 lg:shrink-0">
            {mode === 'online' ? (
              <DarkField label="Camera">
                {(labelId) => (
                  <Select
                    value={preview.cameraId ?? ''}
                    onValueChange={(v) => {
                      preview.setCameraId(v || undefined);
                    }}
                  >
                    <SelectTrigger aria-labelledby={labelId} className={selectTriggerClass}>
                      <SelectValue placeholder="System default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">System default</SelectItem>
                      {preview.devices.cameras.map((c) => (
                        <SelectItem key={c.deviceId} value={c.deviceId}>
                          {c.label || 'Unnamed camera'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </DarkField>
            ) : null}

            <DarkField label="Microphone">
              {(labelId) => (
                <Select
                  value={preview.micId ?? ''}
                  onValueChange={(v) => {
                    preview.setMicId(v || undefined);
                  }}
                >
                  <SelectTrigger aria-labelledby={labelId} className={selectTriggerClass}>
                    <SelectValue placeholder="System default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">System default</SelectItem>
                    {preview.devices.microphones.map((m) => (
                      <SelectItem key={m.deviceId} value={m.deviceId}>
                        {m.label || 'Unnamed microphone'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </DarkField>

            <div className="flex items-center gap-3 pt-1">
              <span className="tracking-label min-w-19.5 text-[11px] text-white/40 uppercase">
                Mic level
              </span>
              <MicLevelBar level={preview.audioLevel} />
            </div>

            <div
              className="flex items-center justify-between rounded-xl border border-white/8 bg-[#0F1F1C]/60 px-4 py-3"
              data-testid="call-status"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="relative flex h-2 w-2 flex-none items-center justify-center">
                  {!isStatusError ? (
                    <span
                      aria-hidden="true"
                      className={`absolute h-full w-full rounded-full ${
                        someoneHere
                          ? 'animate-ping bg-[#6AA489]/70'
                          : 'animate-ping bg-[#D4A017]/60'
                      }`}
                    />
                  ) : null}
                  <span
                    className={`relative inline-flex h-2 w-2 rounded-full ${
                      isStatusError ? 'bg-white/40' : someoneHere ? 'bg-[#6AA489]' : 'bg-[#D4A017]'
                    }`}
                  />
                </span>
                <span className="truncate text-[13px] text-white/75">{waitingCopy}</span>
              </div>
              <button
                type="button"
                onClick={onRefreshStatus}
                className="inline-flex flex-none items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-white/55 transition-colors hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
              >
                <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={1.5} />
                Refresh
              </button>
            </div>

            <div className="mt-auto flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onCancel}
                className="h-11 rounded-xl px-4 text-[13px] text-white/70 transition-colors hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  onJoin({
                    cameraId: preview.cameraId,
                    micId: preview.micId,
                    cameraEnabled: preview.cameraEnabled,
                    micEnabled: preview.micEnabled,
                  });
                }}
                className="group inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#025743] px-6 text-[14px] font-semibold text-white shadow-[0_8px_24px_rgba(2,87,67,0.35)] transition-all hover:bg-[#024E3B] hover:shadow-[0_12px_32px_rgba(2,87,67,0.45)] focus-visible:ring-2 focus-visible:ring-[#6AA489] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1614] focus-visible:outline-none"
              >
                Join now
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                  strokeWidth={2}
                />
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

interface PreviewToggleProps {
  enabled: boolean;
  onClick: () => void;
  label: string;
  OnIcon: typeof Mic;
  OffIcon: typeof MicOff;
}

/**
 * Round mic / camera toggle pill. Sits inside a `pointer-events-none`
 * overlay above the preview tile, so the pill explicitly opts back in
 * via `pointer-events-auto`. Tone shifts to red when muted/blanked so
 * the off state reads instantly even at a glance.
 */
function PreviewToggle({ enabled, onClick, label, OnIcon, OffIcon }: PreviewToggleProps) {
  const Icon = enabled ? OnIcon : OffIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!enabled}
      aria-label={label}
      title={label}
      className={cn(
        'pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors',
        'focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none',
        enabled
          ? 'border-white/15 bg-[#0F1F1C]/80 text-white/85 hover:bg-[#0F1F1C]'
          : 'border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25',
      )}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </button>
  );
}

function DarkField({
  label,
  children,
}: {
  label: string;
  children: (labelId: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <span id={id} className="tracking-label text-[12px] text-white/40 uppercase">
        {label}
      </span>
      {children(id)}
    </div>
  );
}

function BackgroundGlow() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
      <div className="absolute top-[-10%] left-[-10%] h-[500px] w-[500px] rounded-full bg-[radial-gradient(circle,rgba(53,129,110,0.12)_0%,transparent_70%)] blur-3xl" />
      <div className="absolute right-[-15%] bottom-[-20%] h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(3,68,92,0.18)_0%,transparent_70%)] blur-3xl" />
    </div>
  );
}

interface PermissionDeniedStateProps {
  mode: 'online' | 'helpline';
  kind: 'permission-denied' | 'not-found' | 'unknown';
  onRetry: () => void;
  onJoinAudioOnly: () => void;
  onCancel: () => void;
}

function PermissionDeniedState({
  mode,
  kind,
  onRetry,
  onJoinAudioOnly,
  onCancel,
}: PermissionDeniedStateProps) {
  const copy =
    kind === 'permission-denied'
      ? mode === 'online'
        ? 'This browser blocked camera and microphone access. Open your browser permissions, allow both, then retry.'
        : 'This browser blocked microphone access. Open your browser permissions, allow it, then retry.'
      : kind === 'not-found'
        ? 'No usable camera or microphone was found. Check your device or try a different one.'
        : 'Something went wrong accessing your devices. Try again or check your browser console for details.';

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden bg-[#0B1614] p-8 text-white">
      <BackgroundGlow />
      <div className="relative z-10 max-w-md rounded-xl border border-white/8 bg-[#0F1F1C]/80 p-8 text-center backdrop-blur-sm">
        <h2 className="font-display text-[24px] text-white italic">
          Can&rsquo;t access your devices
        </h2>
        <p className="mt-3 text-[13px] leading-relaxed text-white/70">{copy}</p>
        <div className="mt-6 flex items-center justify-center gap-2">
          {kind === 'permission-denied' ? (
            <button
              type="button"
              onClick={onRetry}
              className="h-10 rounded-lg border border-white/15 px-4 text-[13px] text-white/85 transition-colors hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
            >
              Retry
            </button>
          ) : null}
          {kind === 'permission-denied' && mode === 'online' ? (
            <button
              type="button"
              onClick={onJoinAudioOnly}
              className="h-10 rounded-lg border border-white/15 px-4 text-[13px] text-white/85 transition-colors hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:outline-none"
            >
              Join audio-only
            </button>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            className="text-[13px] text-white/55 underline-offset-4 hover:text-white hover:underline focus-visible:outline-none"
          >
            Back to session
          </button>
        </div>
      </div>
    </div>
  );
}
